import { useQuery } from "@tanstack/react-query";
import type { AdminAreaView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

export type AreasListEnvelope = {
  items: AdminAreaView[];
  total: number;
  page: number;
  limit: number;
};

export type UseAreasQueryParams = {
  governorateId?: string;
  /** Server-side search over nameAr/nameEn — must be sent to the API, not
   * filtered client-side, or it would only search the current page. */
  q?: string;
  page?: number;
  limit?: number;
};

function buildAreasPath(params: UseAreasQueryParams): string {
  const search = new URLSearchParams();
  if (params.governorateId) search.set("governorateId", params.governorateId);
  if (params.q) search.set("q", params.q);
  if (params.page) search.set("page", String(params.page));
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `${endpoints.admin.areas}?${qs}` : endpoints.admin.areas;
}

/**
 * Paginated admin areas list. The query key includes every param
 * (governorateId, q, page, limit) so a change to any of them — including
 * page — always misses cache instead of silently serving a stale page.
 */
export function useAreasQuery(params: UseAreasQueryParams = {}) {
  const { governorateId, q, page, limit } = params;
  const query = useQuery({
    queryKey: queryKeys.admin.areas({ governorateId, q, page, limit }),
    queryFn: () => apiJsonClient<AreasListEnvelope>(buildAreasPath(params)),
    placeholderData: (previousData) => previousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
